import type { MenuItem } from './types';

/**
 * Which sidebar item is active for a route path. Shared by the sidebar (navmenu.vue) and the
 * menu-active coverage guard (menu-active-coverage.spec.ts), so the rule "every page opens with
 * an active menu item" is tested against the same logic the sidebar runs.
 *
 * An item matches its own `path` exactly, or any path that starts with one of its
 * `activePathPrefixes` (the fallback for pages without their own menu entry, e.g. edit pages).
 */

export function matchesItemExactPath(item: MenuItem | null | undefined, path: string): boolean {
  return Boolean(item && path && item.path === path);
}

// A `*` in an activePathPrefixes entry matches exactly one path segment, so record pages
// with an id in the middle can alias a menu item (e.g. '/…/outbound/*/resend').
function wildcardPrefixToRegExp(prefix: string): RegExp {
  const pattern = prefix
    .split('*')
    .map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('[^/]+');
  return new RegExp(`^${pattern}`);
}

export function matchesItemPrefix(item: MenuItem | null | undefined, path: string): boolean {
  if (!item || !path || !Array.isArray(item.activePathPrefixes)) return false;
  return item.activePathPrefixes.some((prefix) =>
    prefix.includes('*') ? wildcardPrefixToRegExp(prefix).test(path) : path.startsWith(prefix),
  );
}

export function matchesMenuItemPath(item: MenuItem, path: string): boolean {
  return matchesItemExactPath(item, path) || matchesItemPrefix(item, path);
}

export function containsPath(item: MenuItem, path: string): boolean {
  if (matchesMenuItemPath(item, path)) return true;
  if (!item.children) return false;
  return item.children.some((child) => containsPath(child, path));
}

function findMenuItem(
  items: MenuItem[],
  path: string,
  predicate: (item: MenuItem, path: string) => boolean,
  isVisible: ((item: MenuItem) => boolean) | null,
): MenuItem | null {
  for (const item of items) {
    if (isVisible && !isVisible(item)) continue;
    if (predicate(item, path)) return item;
    if (item.children) {
      const child = findMenuItem(item.children, path, predicate, isVisible);
      if (child) return child;
    }
  }
  return null;
}

// Ordered match: a visible entry beats a hidden one (the same path is often
// declared twice behind different partner gates), and an exact path beats a
// parent's broader `activePathPrefixes` fallback.
export function findMenuItemByPath(
  items: MenuItem[],
  path: string,
  isVisible: (item: MenuItem) => boolean = () => true,
): MenuItem | null {
  for (const visibility of [isVisible, null]) {
    for (const predicate of [matchesItemExactPath, matchesItemPrefix]) {
      const found = findMenuItem(items, path, predicate, visibility);
      if (found) return found;
    }
  }
  return null;
}
